import { replaceAdvancedFilterDraft } from '@/actions/vegobjekterActions';
import {
    AdvancedFilterGroupNode,
    appendAdvancedFilterNode,
    compileAdvancedFilter,
    validateAdvancedFilter,
} from '@/domain/advancedFilter';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { stateUser } from '@/selectors/selectors';
import { VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { useDispatch, useSelector } from '@/state/store';
import * as React from 'react';
import { AdvancedFilterGroupEditor } from './AdvancedFilterGroupEditor';
import {
    AddButtons,
    AddActionKind,
    createNodeForAddAction,
} from './AdvancedFilterAddButtons';
import { AdvancedFilterRawPreview } from './AdvancedFilterRawPreview';

interface Props {
    vegobjekttypeData: VegobjekttypeState;
    egenskapstyper: Egenskapstype[];
    disableSuggestedValueFetch?: boolean;
}

export const AdvancedFilterBuilder: React.FC<Props> = ({
    vegobjekttypeData,
    egenskapstyper,
    disableSuggestedValueFetch = false,
}) => {
    const dispatch = useDispatch();
    const { data } = React.useContext(ConfigAndDataContext);
    const user = useSelector(stateUser);

    const draft = vegobjekttypeData.getAdvancedFilterDraft();
    const applied = vegobjekttypeData.getAdvancedFilterApplied();
    const validation = React.useMemo(() => validateAdvancedFilter(draft), [draft]);

    const allTypes = React.useMemo(() => {
        if (!data) {
            return [];
        }

        return [...data.datakatalog.vegobjekttyper]
            .filter(type => !type.abstrakt_type)
            .sort((a, b) => a.navn.localeCompare(b.navn, 'nb'));
    }, [data]);

    const onDraftChange = React.useCallback(
        (nextDraft: AdvancedFilterGroupNode | null) => {
            dispatch(replaceAdvancedFilterDraft(vegobjekttypeData, nextDraft));
        },
        [dispatch, vegobjekttypeData]
    );

    const onAddTopLevel = React.useCallback(
        (kind: AddActionKind) => {
            onDraftChange(
                appendAdvancedFilterNode(draft, null, createNodeForAddAction(kind))
            );
        },
        [draft, onDraftChange]
    );

    const draftPreview = compileAdvancedFilter(draft);
    const appliedPreview = compileAdvancedFilter(applied);

    return (
        <section className="c-advanced-filter" aria-label="Avansert filterbygger">
            <div className="c-advanced-filter__body">
                {!draft && <AddButtons onAdd={onAddTopLevel} containerDepth={1} />}

                {draft && (
                    <AdvancedFilterGroupEditor
                        root={draft}
                        group={draft}
                        groupDepth={1}
                        isRoot
                        egenskapstyper={egenskapstyper}
                        vegobjekttypeData={vegobjekttypeData}
                        datakatalogVegobjekttyper={allTypes}
                        errorsByNodeId={validation.errorsByNodeId}
                        onDraftChange={onDraftChange}
                        user={user}
                        disableSuggestedValueFetch={disableSuggestedValueFetch}
                    />
                )}
            </div>

            <AdvancedFilterRawPreview
                draftPreview={draftPreview}
                appliedPreview={appliedPreview}
                isValid={validation.valid}
            />
        </section>
    );
};
